// Cancel / reopen a signed contract — ONE flow for bizSAFE / SHMS and client Internal Audit jobs.
//
// Cancelling sets the job to 'Cancelled' (reason, who and when appended to the job notes) and every SIGNED quotation
// for that job to 'Cancelled' (its signed date and signature record are kept). Reopening reverses both.
//   • Quotations are updated first; if the job update then fails they are put back, so the two never disagree.
//   • A cancelled quotation is history: off the Quotations working lists, counted as lost on the Tracker, and its
//     signing link says "cancelled" (a database rule — sql/CANCEL_CONTRACT.sql — also refuses re-signing).
//   • The job leaves the Active jobs list, the Dashboard, the Tracker, the SAC audit list and the auditor's
//     My Audits; the client's acknowledgement / plan link and the auditors' COI link say "cancelled".
// The office tells the auditor(s) and the client — nothing is emailed from here.
import { format } from 'date-fns'
import { supabase } from './supabase'

const today = () => format(new Date(), 'd MMM yyyy')

// The quotations behind a job: linked by job_id, or by the same quote reference (online signing creates the job
// from the quote_ref). Both lookups, merged — a quote_ref can contain '/', so no PostgREST "or" string.
async function quotesFor(job, status) {
  const out = new Map()
  const { data: a, error: e1 } = await supabase.from('quotations').select('id, quote_ref, status').eq('job_id', job.id).eq('status', status)
  if (e1) throw new Error('Could not read the quotation: ' + e1.message)
  ;(a || []).forEach(q => out.set(q.id, q))
  if (job.quote_ref) {
    const { data: b, error: e2 } = await supabase.from('quotations').select('id, quote_ref, status').eq('quote_ref', job.quote_ref).eq('status', status)
    if (e2) throw new Error('Could not read the quotation: ' + e2.message)
    ;(b || []).forEach(q => out.set(q.id, q))
  }
  return [...out.values()]
}

async function setQuotes(ids, status) {
  if (!ids.length) return
  const { error } = await supabase.from('quotations').update({ status }).in('id', ids)
  if (error) throw new Error('Could not update the quotation: ' + error.message)
}

// The latest "Cancelled by … : reason" line from a job's notes (shown on the job page).
export function lastCancelNote(notes) {
  return String(notes || '').split('\n').filter(l => /^Cancelled by /.test(l)).pop() || ''
}

export async function cancelContract({ job, reason, by }) {
  const quotes = await quotesFor(job, 'Signed')
  await setQuotes(quotes.map(q => q.id), 'Cancelled')
  const note = `Cancelled by ${by || 'Staff'} on ${today()}: ${reason}`
  const { error } = await supabase.from('jobs').update({ status: 'Cancelled', notes: [job.notes, note].filter(Boolean).join('\n') }).eq('id', job.id)
  if (error) {
    try { await setQuotes(quotes.map(q => q.id), 'Signed') } catch { /* reported below */ }
    throw new Error('Could not cancel: ' + error.message)
  }
  return { quoteRefs: quotes.map(q => q.quote_ref) }
}

export async function reopenContract({ job, status, by }) {
  const quotes = await quotesFor(job, 'Cancelled')
  await setQuotes(quotes.map(q => q.id), 'Signed')
  const note = `Reopened by ${by || 'Staff'} on ${today()}`
  const { error } = await supabase.from('jobs').update({ status, notes: [job.notes, note].filter(Boolean).join('\n') }).eq('id', job.id)
  if (error) {
    try { await setQuotes(quotes.map(q => q.id), 'Cancelled') } catch { /* reported below */ }
    throw new Error('Could not reopen: ' + error.message)
  }
  return { quoteRefs: quotes.map(q => q.quote_ref), quotes }
}
